import { useEffect, useState } from "react";
import { getLeadsSources, leadsSourcesTrend } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight, Megaphone, Globe, CalendarCheck, DoorOpen, Users, Target } from "lucide-react";
import { numberLocale } from "@/lib/money";

const fmtN = (n) => new Intl.NumberFormat(numberLocale()).format(n || 0);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dateShort = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

// Leads coming into this gym, counted automatically (backend routes/lead_log.py): every new lead in
// the gym's GoHighLevel lead pipeline (sorted by its source), consults booked straight from the
// calendar, walk-ins (a pre-sale questionnaire from someone who hadn't enquired) and referrals.
const SOURCES = [
  { key: "ads", label: "Ads", icon: Megaphone, color: "#f43f5e", text: "text-rose-300", hint: "Meta (Facebook / Instagram) lead forms and other GoHighLevel leads" },
  { key: "website", label: "Website", icon: Globe, color: "#a78bfa", text: "text-violet-300", hint: "Website lead forms" },
  { key: "booked", label: "Booked online", icon: CalendarCheck, color: "#fbbf24", text: "text-amber-300", hint: "Booked a consult straight from the calendar, without enquiring first" },
  { key: "walk_in", label: "Walk-in", icon: DoorOpen, color: "#60a5fa", text: "text-blue-300", hint: "Filled the pre-sale questionnaire at the gym without enquiring first" },
  { key: "referral", label: "Referral", icon: Users, color: "#34d399", text: "text-emerald-300", hint: "Referred by a member (consult or GoHighLevel source)" },
];

export default function Leads() {
  const { activeFacility } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([getLeadsSources(year, month), leadsSourcesTrend(12)]).then(([d, t]) => {
      if (!live) return;
      setData(d);
      setTrend(t.points || []);
    }).catch(() => {});
    return () => { live = false; };
  }, [year, month, activeFacility?.id]);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };
  const weeks = data?.weeks || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-8" data-testid="leads-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Leads by source · {activeFacility?.name || "this gym"}</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Leads · {MONTHS[month - 1]} {year}</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Counted automatically from GoHighLevel, the pre-sale questionnaire and consults. Nobody needs to type these in.
            The month total feeds cost per lead on the Dashboard.
          </p>
        </div>
        <div className="flex items-center gap-1 card-surface p-1 rounded-full">
          <Button variant="ghost" size="sm" onClick={() => shift(-1)} className="rounded-full h-8 w-8 p-0" data-testid="leads-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="px-3 text-sm font-medium tabular-nums" data-testid="leads-current-period">{MONTHS[month - 1]} {year}</div>
          <Button variant="ghost" size="sm" onClick={() => shift(1)} className="rounded-full h-8 w-8 p-0" data-testid="leads-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {SOURCES.map((s) => (
          <div key={s.key} className="card-surface p-4" title={s.hint}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5"><s.icon className={`h-3 w-3 ${s.text}`} />{s.label}</div>
            <div className={`font-display text-2xl font-extrabold mt-1 tabular-nums ${s.text}`} data-testid={`leads-total-${s.key}`}>{fmtN(totals[s.key])}</div>
          </div>
        ))}
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5"><Target className="h-3 w-3 text-blue-400" />Total</div>
          <div className="font-display text-2xl font-extrabold mt-1 tabular-nums text-blue-400" data-testid="leads-total-month">{fmtN(totals.total)}</div>
        </div>
      </div>

      <section className="card-surface overflow-hidden" data-testid="leads-weekly-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-4 py-3 font-medium">Week</th>
                <th className="px-4 py-3 font-medium">Dates</th>
                {SOURCES.map((s) => (
                  <th key={s.key} className="px-4 py-3 font-medium text-right" title={s.hint}>
                    <span className="inline-flex items-center gap-1.5"><s.icon className={`h-3 w-3 ${s.text}`} />{s.label}</span>
                  </th>
                ))}
                <th className="px-4 py-3 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week} className="border-b border-[#1a1a1a] last:border-0" data-testid={`leads-week-row-${w.week}`}>
                  <td className="px-4 py-3 font-medium tabular-nums text-zinc-300">W{w.week}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">{dateShort(w.start)} <span className="text-zinc-600">→</span> {dateShort(w.end)}</td>
                  {SOURCES.map((s) => (
                    <td key={s.key} className={`px-4 py-3 text-right font-mono tabular-nums ${w[s.key] ? s.text : "text-zinc-700"}`}>{fmtN(w[s.key])}</td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono text-blue-300 font-semibold tabular-nums" data-testid={`leads-week-total-${w.week}`}>{fmtN(w.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {data?.since && <p className="text-xs text-zinc-500">GoHighLevel leads are counted from {dateShort(data.since)}. Walk-ins from the pre-sale questionnaire (since 9 Oct 2026).</p>}

      <section className="card-surface p-6" data-testid="leads-trend-chart">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow">Leads by source · last 12 months</div>
            <h3 className="font-display text-xl font-bold mt-1">Where this gym's leads come from</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            {SOURCES.map((s) => <span key={s.key} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />{s.label}</span>)}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} />
              {SOURCES.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.color} name={s.label} radius={i === SOURCES.length - 1 ? [4, 4, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
